/**
 * Shared date formatting utilities for all themes
 * 
 * This module provides reusable date formatting functions and utilities
 * that are consistent across all themes in the application.
 * 
 * @module dateFormatter
 */

import dayjs from '@shared/plugins/dayjs-config';

/**
 * Validates a date range object with start and end properties
 * 
 * @param {Object} dateRange - Object with start and end date properties
 * @param {Date|string} dateRange.start - Start date
 * @param {Date|string} dateRange.end - End date
 * @throws {Error} If dateRange is invalid
 * 
 * @example
 * validateDateRange({ start: '2024-01-01', end: '2024-01-31' }); // OK
 * validateDateRange({ start: '2024-02-01', end: '2024-01-01' }); // Throws: Start date must be before end date
 */
export function validateDateRange(dateRange) {
  if (!dateRange || typeof dateRange !== 'object') {
    throw new Error('Date range must be an object.');
  }

  if ('start' in dateRange && 'end' in dateRange) {
    const start = dayjs(dateRange.start);
    const end = dayjs(dateRange.end);

    if (!start.isValid() || !end.isValid()) {
      throw new Error('Date range contains invalid dates.');
    }

    if (start.isAfter(end)) {
      throw new Error('Start date must be before end date.');
    }
  }
}

export class DateFormatter {
  constructor(language) {
    this.language = language;
    this.formatOptions = {
    };
  }

  setFormatYear(yearFormat) {
    this.formatOptions.year = yearFormat;
  }

  setFormatMonth(monthFormat) {
    this.formatOptions.month = monthFormat;
  }

  setFormatDay(dayFormat) {
    this.formatOptions.day = dayFormat;
  }

  setFormatHour(hourFormat) {
    this.formatOptions.hour = hourFormat;
    this.formatOptions.hour12 = false;
  }

  setFormatMinute(minuteFormat) {
    this.formatOptions.minute = minuteFormat;
  }
  
  format (dateString) {
    const date  = new Date(dateString);
    const locale = `${this.language}-${this.language.toUpperCase()}`;
    
    return date.toLocaleDateString(locale, this.formatOptions);
  }
}

// Example: Feb 08
export function getCompactMonthDateFormatter (dateString, language) {
  const dateFormatter = new DateFormatter(language);
  dateFormatter.setFormatMonth('short');
  dateFormatter.setFormatDay('2-digit');
  return dateFormatter.format(dateString);
}

// Example: Jun 30, 2019, 18:22
export function getCompactMonthDateYearAndTimeFormatter (dateString, language) {
  const dateFormatter = new DateFormatter(language);
  dateFormatter.setFormatYear('numeric');
  dateFormatter.setFormatMonth('short');
  dateFormatter.setFormatDay('2-digit');
  dateFormatter.setFormatHour('2-digit');
  dateFormatter.setFormatMinute('2-digit');

  return dateFormatter.format(dateString);
}

// Example: 2022-09-02 => 2022-09-02T00:00:00+03:00
export function getStartOfDay(dateString){
  return dayjs(dateString).startOf('day').format('YYYY-MM-DDTHH:mm:ssZ');
}

// Example: 2022-09-02 => 2022-09-02T23:59:59+03:00
export function getEndOfDay(dateString){
  return dayjs(dateString).endOf('day').format('YYYY-MM-DDTHH:mm:ssZ');
}

/**
 * Check if a date is today
 * 
 * @param {Date|string|number} date - Date to check
 * @returns {boolean} True if date is today, false if date is invalid/null
 * 
 * @example
 * isToday(new Date()); // true
 * isToday('2024-01-15'); // false (unless today is 2024-01-15)
 * isToday(null); // false
 */
export function isToday(date) {
  if (!date) return false;
  const dayjsDate = dayjs(date);
  if (!dayjsDate.isValid()) return false;
  return dayjsDate.isSame(dayjs(), 'day');
}

/**
 * Check if a date is yesterday
 * 
 * @param {Date|string|number} date - Date to check
 * @returns {boolean} True if date is yesterday, false if date is invalid/null
 * 
 * @example
 * const yesterday = new Date();
 * yesterday.setDate(yesterday.getDate() - 1);
 * isYesterday(yesterday); // true
 * isYesterday(null); // false
 */
export function isYesterday(date) {
  if (!date) return false;
  const dayjsDate = dayjs(date);
  if (!dayjsDate.isValid()) return false;
  const yesterday = dayjs().subtract(1, 'day');
  return dayjsDate.isSame(yesterday, 'day');
}

